import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { formatDateRange } from "../lib/constants";
import { Button } from "../components/ui/button";
import { CalendarBlank, MapPin, Users, ArrowLeft, Globe, Check, Heart, Fire } from "@phosphor-icons/react";
import GoogleEventsMap from "../components/GoogleEventsMap";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import PromoteEventModal from "../components/PromoteEventModal";

const redIcon = null; // legacy - now handled by GoogleEventsMap

export default function EventDetail() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const [attending, setAttending] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);

  useEffect(() => {
    api.get(`/events/${id}`).then(({ data }) => {
      setEvent(data);
      if (user && data.attendees?.includes(user.user_id)) setAttending(true);
    }).catch(() => toast.error("Evento non trovato")).finally(() => setLoading(false));
  }, [id, user]);

  const toggleAttend = async () => {
    if (!user) { toast.error("Accedi per partecipare"); return; }
    try {
      const { data } = await api.post(`/events/${id}/attend`);
      setAttending(data.attending);
      // refetch counts
      const { data: e } = await api.get(`/events/${id}`);
      setEvent(e);
      toast.success(data.attending ? "Partecipazione confermata" : "Partecipazione rimossa");
    } catch {
      toast.error("Errore");
    }
  };

  if (loading) return <div className="p-12 text-center text-neutral-500 uppercase tracking-widest text-sm">Caricamento...</div>;
  if (!event) return <div className="p-12 text-center">Evento non trovato</div>;

  return (
    <div>
      <div className="relative h-[60vh] min-h-[400px] overflow-hidden">
        {event.cover_image && (
          <img src={event.cover_image} alt={event.title} className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/20" />
        <div className="absolute inset-0 grain-overlay" />
        <div className="absolute top-6 left-6">
          <Button asChild variant="ghost" size="sm" className="text-white hover:text-primary bg-black/50 backdrop-blur border border-white/20" data-testid="back-btn">
            <Link to="/events"><ArrowLeft size={14} weight="bold" className="mr-2" /> Indietro</Link>
          </Button>
        </div>
        <div className="absolute bottom-0 left-0 right-0 max-w-[1600px] mx-auto px-6 lg:px-12 pb-12">
          <div className="inline-block px-2 py-1 bg-primary text-white text-[10px] font-black uppercase tracking-widest mb-4">
            {event.category}
          </div>
          {event.is_featured && (
            <div className="inline-flex items-center gap-1 ml-2 px-2 py-1 bg-white text-black text-[10px] font-black uppercase tracking-widest">
              <Fire size={10} weight="fill" className="text-primary" /> Featured
            </div>
          )}
          <h1 className="font-display text-4xl sm:text-6xl font-black uppercase tracking-tighter leading-none max-w-4xl" data-testid="event-title">{event.title}</h1>
          <div className="mt-6 flex flex-wrap gap-6 text-sm text-neutral-200">
            <div className="flex items-center gap-2"><CalendarBlank size={16} weight="fill" className="text-primary" /><span className="font-semibold">{formatDateRange(event.date_start, event.date_end)}</span></div>
            <div className="flex items-center gap-2"><MapPin size={16} weight="fill" className="text-primary" /><span>{event.location_name}, {event.city}, {event.country}</span></div>
            <div className="flex items-center gap-2"><Users size={16} weight="fill" className="text-primary" /><span>{(event.attendees || []).length} partecipanti</span></div>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-16 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-4">About</div>
          <p className="text-neutral-200 leading-relaxed text-lg" data-testid="event-description">{event.description}</p>

          {event.brands?.length > 0 && (
            <div className="mt-12">
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-4">Marche</div>
              <div className="flex flex-wrap gap-2">
                {event.brands.map(b => (
                  <span key={b} className="px-3 py-1.5 border border-border text-sm uppercase tracking-wider text-neutral-200">{b}</span>
                ))}
              </div>
            </div>
          )}

          {event.lat && event.lng && (
            <div className="mt-12">
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-4">Location</div>
              <GoogleEventsMap events={[event]} height="400px" />
            </div>
          )}
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="border border-border rounded-sm p-6 bg-card">
            <Button
              onClick={toggleAttend}
              data-testid="attend-btn"
              className={`w-full h-14 font-black uppercase tracking-wider rounded-sm ${attending ? "bg-neutral-800 hover:bg-neutral-700 text-white" : "bg-primary hover:bg-primary/90 text-white"}`}
            >
              {attending ? <><Check size={16} weight="bold" className="mr-2" /> Partecipo</> : <><Heart size={16} weight="fill" className="mr-2" /> Partecipa</>}
            </Button>
            {event.website && (
              <Button asChild variant="outline" className="w-full mt-3 h-12 border-border rounded-sm uppercase tracking-wider text-xs font-bold">
                <a href={event.website} target="_blank" rel="noopener noreferrer" data-testid="event-website-link">
                  <Globe size={14} weight="bold" className="mr-2" /> Sito ufficiale
                </a>
              </Button>
            )}
            {user && event.created_by === user.user_id && (
              <Button
                onClick={() => setPromoteOpen(true)}
                data-testid="promote-event-btn"
                variant="outline"
                className="w-full mt-3 h-12 border-primary text-primary hover:bg-primary hover:text-white rounded-sm uppercase tracking-wider text-xs font-bold"
              >
                <Fire size={14} weight="fill" className="mr-2" /> {event.is_featured ? "Estendi Boost" : "Metti in evidenza"}
              </Button>
            )}
            <div className="mt-6 pt-6 border-t border-white/5 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-neutral-500 uppercase tracking-widest text-[10px]">Categoria</span>
                <span className="font-semibold">{event.category}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-500 uppercase tracking-widest text-[10px]">Città</span>
                <span className="font-semibold">{event.city}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-500 uppercase tracking-widest text-[10px]">Paese</span>
                <span className="font-semibold">{event.country}</span>
              </div>
            </div>
          </div>
        </aside>
      </div>
      <PromoteEventModal open={promoteOpen} onOpenChange={setPromoteOpen} event={event} />
    </div>
  );
}
